import { Badge, Button } from "@design";
import { FileText } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Text } from "@/components/Text";
import { AcceptAllModal } from "./AcceptAllModal";
import { type KnowledgeImport, useDismissAll } from "./useImports";

export interface ImportGroupInfo {
  importUuid: string;
  filename: string;
  spaceUuid: string;
  count: number;
}

/** The facts from one imported document, with bulk accept / dismiss above them. */
export function ImportGroup({ group, children }: Readonly<{ group: ImportGroupInfo; children: ReactNode }>) {
  const { t } = useTranslation("knowledge");
  const [accepting, setAccepting] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const dismiss = useDismissAll();
  const label = t("import.groupLabel", { filename: group.filename });

  return (
    <section aria-label={label} className="flex flex-col gap-3">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-center gap-2">
          <FileText size={18} aria-hidden className="shrink-0 text-content-tertiary" />
          <Text as="h2" variant="h3" className="min-w-0 break-words">
            {label}
          </Text>
          <Badge variant="neutral">{t("import.groupCount", { count: group.count })}</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={() => setDismissing(true)}>
            {t("import.dismissAll")}
          </Button>
          <Button variant="primary" size="sm" onClick={() => setAccepting(true)}>
            {t("import.acceptAll")}
          </Button>
        </div>
      </header>
      {children}
      {accepting && (
        <AcceptAllModal
          importUuid={group.importUuid}
          filename={group.filename}
          count={group.count}
          defaultSpaceUuid={group.spaceUuid}
          onClose={() => setAccepting(false)}
        />
      )}
      <ConfirmDialog
        open={dismissing}
        title={t("import.dismissAllTitle", { filename: group.filename })}
        body={t("import.dismissAllBody", { count: group.count })}
        confirmLabel={t("import.dismissAll")}
        loading={dismiss.isPending}
        onClose={() => setDismissing(false)}
        onConfirm={() =>
          dismiss.mutate({ path: { import_uuid: group.importUuid } }, { onSettled: () => setDismissing(false) })
        }
      />
    </section>
  );
}

/** Imports still reading their document: shown above the inbox so new facts don't appear unexplained. */
export function RunningImports({ imports }: Readonly<{ imports: KnowledgeImport[] }>) {
  const { t } = useTranslation("knowledge");
  if (imports.length === 0) return null;
  return (
    <ul aria-label={t("import.progressLabel")} className="flex flex-col gap-1">
      {imports.map((row) => (
        <li key={row.uuid}>
          <Text variant="body-sm" tone="secondary">
            {row.progress.partsTotal > 0
              ? t("import.runningParts", {
                  filename: row.filename,
                  done: row.progress.partsDone,
                  total: row.progress.partsTotal,
                })
              : t("import.running", { filename: row.filename })}
          </Text>
        </li>
      ))}
    </ul>
  );
}
